import React from 'react';

import { Box } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { LinkProvider } from '@opendoor/atoms/utils';
import { useAuth } from '@opendoor/auth-fe/context';
import { IconArrowBarToRight, IconHelp, IconHome, IconUserCircle } from '@tabler/icons-react';
import { getCustomerNavigationHref } from 'components/account/impersonation';
import { navTransactionIcon } from 'components/account/navTransactionIcon';
import { CONSUMER_URL } from 'components/globals';
import { dashboardHref } from 'helpers/dashboardSession';
import { navTransactionLabel, useNavTransactions } from 'helpers/useNavTransactions';

import { MenuLinkItem } from '../components/MenuLinkItem';

interface MobileMenuAccountItemsProps {
  slug: string;
}

const isDashboardPath = (slug: string): boolean => {
  const normalizedSlug = slug.replace(/^\/+/, '');
  return normalizedSlug === 'dashboard' || normalizedSlug.startsWith('dashboard/');
};

export const MobileMenuAccountItemsV2: React.FC<MobileMenuAccountItemsProps> = ({ slug }) => {
  const transactions = useNavTransactions();
  const { logout } = useAuth();

  return (
    <>
      <MenuLinkItem
        variant="account"
        key="dashboard"
        href={dashboardHref('mobile_menu')}
        name="Dashboard"
        icon={<IconHome size={20} stroke={1.5} aria-hidden />}
        analyticsName="cosmos-mobile-nav-dashboard"
        isActive={isDashboardPath(slug)}
      />
      {transactions.map((transaction) => {
        const Icon = navTransactionIcon(transaction.type);
        return (
          <MenuLinkItem
            variant="account"
            key={transaction.id}
            href={transaction.href}
            name={navTransactionLabel(transaction)}
            icon={<Icon size={20} stroke={1.5} aria-hidden />}
            analyticsName={`cosmos-mobile-nav-${transaction.type}`}
            isActive={false}
          />
        );
      })}
      <Box aria-hidden className="py-2">
        <Box className="h-px w-full bg-neutral-200" />
      </Box>
      {/* Account settings are served by the consumer app. */}
      <LinkProvider>
        <MenuLinkItem
          variant="account"
          href={getCustomerNavigationHref(`${CONSUMER_URL}/account`)}
          name="Account"
          icon={<IconUserCircle size={20} stroke={1.5} aria-hidden />}
          analyticsName=""
          isActive={slug === '/account'}
        />
      </LinkProvider>
      <Box aria-hidden className="py-2">
        <Box className="h-px w-full bg-od-border-secondary" />
      </Box>
      <MenuLinkItem
        variant="account"
        href="https://help.opendoor.com"
        name="Help center"
        analyticsName="cosmos-mobile-nav-help-center"
        icon={<IconHelp size={20} stroke={1.5} aria-hidden />}
        isLast
      />
      <Box
        onClick={() => logout()}
        aria-label="Sign out"
        analyticsName="cosmos-account-menu-sign-out"
        className="min-h-12 w-full flex-row items-center gap-2 rounded-lg py-3 pr-1 text-left text-od-content-primary hover:bg-od-bg-hover-primary"
      >
        <IconArrowBarToRight size={20} stroke={1.5} aria-hidden />
        <Text as="span" className="text-body-md">
          Sign out
        </Text>
      </Box>
    </>
  );
};
